import { Button, Snackbar, Alert } from '@locus-taxy/disco-ui';
import { useEffect, useMemo, useState } from 'react';
import {
  SchemaModalBase,
  SchemaContent,
  ActionSelector,
} from '@locus-taxy/encore-grid';
import { useCopyFeedback } from '../hooks/useCopyFeedback';
import type { EntityMetadataResponse } from '../api/endpoints/useEntityMetadata';
import type { EntityDetailsAction } from '../types/actions';
import { getTestId } from '@locus-taxy/encore-utils';
import { useTranslation } from '@locus-taxy/encore-i18n-toolkit';
import { TestIdAction } from '../utils/testIdUtils';

interface DetailsSchemaModalProps {
  open: boolean;
  onClose: () => void;
  entityMetadata: EntityMetadataResponse | undefined;
  entityName: string;
  actions: EntityDetailsAction[];
  entityData?: Record<string, unknown>;
}

const SEVERITY_ALERT_VARIANT = {
  success: 'success',
  error: 'destructive',
} as const;

export default function DetailsSchemaModal({
  open,
  onClose,
  entityMetadata,
  entityName,
  actions,
  entityData,
}: Readonly<DetailsSchemaModalProps>) {
  const { t } = useTranslation();
  const { copyFeedback, handleCopy, closeCopyFeedback } = useCopyFeedback();
  const [activeTab, setActiveTab] = useState<
    'details' | 'actions' | 'data' | 'metadata'
  >('details');
  const [selectedAction, setSelectedAction] = useState<string>('');

  // Labels reused across the tab buttons and their panels — resolved once.
  const detailsViewFormLabel = t(
    'dashboard.schemaModal.detailsViewForm',
    'Details View Form'
  );
  const detailsViewActionsLabel = t(
    'dashboard.schemaModal.detailsViewActions',
    'Details View Actions'
  );
  const entityDataLabel = t('dashboard.schemaModal.entityData', 'Entity Data');
  const metadataLabel = t('dashboard.schemaModal.metadata', 'Metadata');

  const detailsForm = entityMetadata?.data.frontend?.detailsView?.form?.schema;

  const availableActions = useMemo(() => actions ?? [], [actions]);

  useEffect(() => {
    if (selectedAction === '' && availableActions.length > 0) {
      setSelectedAction(availableActions[0].id);
    }
  }, [selectedAction, availableActions]);

  if (!entityMetadata) {
    return null;
  }

  const tabButtons = (
    <>
      <Button
        variant={activeTab === 'details' ? 'default' : 'outline'}
        size="sm"
        onClick={() => setActiveTab('details')}
        data-testid={getTestId({
          action: TestIdAction.TAB,
          object: 'details',
          entity: entityName,
          page: 'details',
        })}
      >
        {detailsViewFormLabel}
      </Button>
      {availableActions.length > 0 && (
        <Button
          variant={activeTab === 'actions' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveTab('actions')}
          data-testid={getTestId({
            action: TestIdAction.TAB,
            object: 'actions',
            entity: entityName,
            page: 'details',
          })}
        >
          {detailsViewActionsLabel}
        </Button>
      )}
      {entityData && (
        <Button
          variant={activeTab === 'data' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveTab('data')}
          data-testid={getTestId({
            action: TestIdAction.TAB,
            object: 'entity-data',
            entity: entityName,
            page: 'details',
          })}
        >
          {entityDataLabel}
        </Button>
      )}
      <Button
        variant={activeTab === 'metadata' ? 'default' : 'outline'}
        size="sm"
        onClick={() => setActiveTab('metadata')}
        data-testid={getTestId({
          action: TestIdAction.TAB,
          object: 'metadata',
          entity: entityName,
          page: 'details',
        })}
      >
        {metadataLabel}
      </Button>
    </>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'details':
        return (
          <SchemaContent
            title={detailsViewFormLabel}
            data={
              detailsForm as Record<string, unknown> | unknown[] | undefined
            }
            copyLabel={detailsViewFormLabel}
            onCopy={handleCopy}
          />
        );
      case 'actions':
        return (
          <ActionSelector
            title={detailsViewActionsLabel}
            actions={availableActions}
            selectedAction={selectedAction}
            onActionChange={setSelectedAction}
            onCopy={handleCopy}
          />
        );
      case 'data':
        return (
          <SchemaContent
            title={entityDataLabel}
            data={entityData}
            copyLabel={entityDataLabel}
            onCopy={handleCopy}
          />
        );
      case 'metadata':
        return (
          <SchemaContent
            title={metadataLabel}
            data={entityMetadata.data as Record<string, unknown>}
            copyLabel={metadataLabel}
            onCopy={handleCopy}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <SchemaModalBase
        open={open}
        onClose={onClose}
        entityName={entityName}
        tabButtons={tabButtons}
      >
        {renderContent()}
      </SchemaModalBase>

      <Snackbar
        open={copyFeedback.show}
        autoHideDuration={3000}
        onClose={closeCopyFeedback}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          variant={SEVERITY_ALERT_VARIANT[copyFeedback.severity]}
          className="w-full"
        >
          {copyFeedback.message}
        </Alert>
      </Snackbar>
    </>
  );
}
